import { useEffect, useState, useCallback } from 'react';
import { Dumbbell, Check, Save, MessageSquareText, Play, CheckCircle2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { api, getStoredUser, errMsg, hasEntitlement, getEntitlements } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { LoadingScreen, EmptyState } from '@/components/State';
import { isValidYouTube } from '@/lib/youtube';
import VideoPlayerModal from '@/components/VideoPlayerModal';
import LockedFeature from '@/components/LockedFeature';

// DE -> German original field, EN -> stored *_en field (falls back to German).
const pick = (obj, key, lang) => (lang === 'en' ? (obj?.[`${key}_en`] || obj?.[key]) : obj?.[key]);

export default function Training() {
  const { t, lang } = useI18n();
  const [plan, setPlan] = useState(null);
  const [progress, setProgress] = useState({ active_week: 1, completed_weeks: [] });
  const [loading, setLoading] = useState(true);
  const [weekIdx, setWeekIdx] = useState(0);
  const [dayIdx, setDayIdx] = useState(0);
  const user = getStoredUser();
  const allowed = user?.role === 'admin' || hasEntitlement('training_access');

  const load = useCallback(async () => {
    if (!allowed) { setLoading(false); return; }
    setLoading(true);
    try {
      const [p, pr] = await Promise.all([
        api.get(`/training-plans/${user.id}`),
        api.get(`/client-progress/${user.id}`).catch(() => ({ data: { active_week: 1, completed_weeks: [] } })),
      ]);
      setPlan(p.data);
      setProgress(pr.data || { active_week: 1, completed_weeks: [] });
      const active = (pr.data?.active_week || 1) - 1;
      const weeks = p.data?.weeks || [];
      setWeekIdx(Math.max(0, Math.min(active, weeks.length - 1)));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!allowed) return <LockedFeature testid="training-locked" reason={(getEntitlements() || []).length === 0 ? 'package' : 'entitlement'} />;
  if (loading) return <div className="px-6 pt-10"><LoadingScreen /></div>;

  const weeks = plan?.weeks || (plan?.days ? [{ number: 1, days: plan.days }] : []);
  if (!weeks.length || !(weeks[weekIdx]?.days || []).length) {
    return (
      <div className="px-6 pt-10">
        <Header t={t} />
        <EmptyState icon={Dumbbell} title={t('training.title')} sub={t('training.empty')} />
      </div>
    );
  }

  const week = weeks[weekIdx];
  const day = week.days[Math.min(dayIdx, week.days.length - 1)];
  const activeWeekNo = progress.active_week || 1;
  const completed = progress.completed_weeks || [];
  const weekNo = week.number || weekIdx + 1;
  const isLocked = user?.role !== 'admin' && weekNo !== activeWeekNo;
  const isCompleted = completed.includes(weekNo);

  const confirmWeek = async () => {
    try {
      await api.post('/training-plans/me/confirm-week', { week_no: weekNo });
      toast.success(`${t('training.week')} ${weekNo} ✓`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="px-6 pt-10 animate-fade-in" data-testid="client-training">
      <Header t={t} />

      {weeks.length > 1 && (
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
          {weeks.map((w, i) => {
            const n = w.number || i + 1;
            const done = completed.includes(n);
            const act = n === activeWeekNo;
            return (
              <button key={i} data-testid={`week-tab-${n}`} onClick={() => { setWeekIdx(i); setDayIdx(0); }}
                className={`as-chip whitespace-nowrap ${weekIdx === i ? 'border-asgym-accent bg-asgym-accent/15 text-asgym-accent' : 'border-white/[0.08] bg-white/[0.03] text-asgym-sub'}`}>
                {done && <Check size={12} />} {act && !done && <span className="h-1.5 w-1.5 rounded-full bg-asgym-accent" />}
                {t('training.week')} {n}
              </button>
            );
          })}
        </div>
      )}

      <div className="mb-5 flex gap-2 overflow-x-auto pb-1">
        {week.days.map((d, i) => (
          <button key={d.id || i} data-testid={`day-tab-${i}`} onClick={() => setDayIdx(i)}
            className={`as-chip whitespace-nowrap ${dayIdx === i ? 'border-asgym-accent bg-asgym-accent/15 text-asgym-accent' : 'border-white/[0.08] bg-white/[0.03] text-asgym-sub'}`}>
            {pick(d, 'name', lang) || `${t('training.day')} ${i + 1}`}
          </button>
        ))}
      </div>

      {isLocked && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-asgym-warn/30 bg-asgym-warn/10 p-3 text-xs font-bold text-asgym-warn">
          <Lock size={14} /> {t('training.week')} {activeWeekNo} {t('common.active')?.toLowerCase?.() || ''}
        </div>
      )}

      <div className="space-y-3">
        {(day.exercises || []).map((ex, i) => (
          <ExerciseCard key={ex.id || i} ex={ex} planId={plan.id} dayId={day.id} locked={isLocked} t={t} lang={lang} onSaved={load} />
        ))}
      </div>

      {!isLocked && !isCompleted && (
        <button data-testid="confirm-week-btn" onClick={confirmWeek} className="as-btn-primary mt-6 mb-6 w-full">
          <CheckCircle2 size={18} /> {t('training.confirmWeek')}
        </button>
      )}
      {isCompleted && (
        <div className="mt-6 mb-6 flex items-center justify-center gap-2 rounded-xl border border-asgym-accent/30 bg-asgym-accent/10 p-4 text-sm font-bold text-asgym-accent">
          <CheckCircle2 size={18} /> {t('training.week')} {weekNo} ✓
        </div>
      )}
    </div>
  );
}

const Header = ({ t }) => (
  <div className="mb-6">
    <p className="as-label">{t('training.yourPlan')}</p>
    <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-white">{t('training.title')}</h1>
  </div>
);

function ExerciseCard({ ex, planId, dayId, locked, t, lang, onSaved }) {
  const [sets, setSets] = useState(() => (ex.sets || []).map((s) => ({
    weight: s.actual_weight ?? s.weight ?? '',
    reps: s.actual_reps ?? s.reps ?? '',
    done: !!(s.completed ?? s.done),
  })));
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(ex.client_note || '');
  const [videoOpen, setVideoOpen] = useState(false);
  const video = (ex.resolved_video_url || ex.video_override_url || ex.video_url || '').trim();
  const hasVideo = isValidYouTube(video);

  const upd = (i, k, v) => setSets((p) => p.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/training-logs', {
        plan_id: planId,
        day_id: dayId,
        exercise_id: ex.id,
        exercise_name: ex.name,
        exercise_library_id: ex.exercise_library_id || null,
        client_note: note,
        sets: sets.map((s) => ({
          actual_weight: s.weight === '' ? null : parseFloat(s.weight),
          actual_reps: s.reps === '' ? null : parseInt(s.reps, 10),
          completed: s.done,
        })),
      });
      toast.success(t('training.saved'));
      onSaved?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="as-card p-4" data-testid="exercise-card">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.04] text-sm font-black text-asgym-accent">{ex.no || ''}</span>
          <div>
            <p className="text-[15px] font-bold text-white">{pick(ex, 'name', lang)}</p>
            {pick(ex, 'device', lang) && <p className="text-[11px] font-semibold text-asgym-muted">{pick(ex, 'device', lang)}</p>}
          </div>
        </div>
        {hasVideo ? (
          <button
            onClick={() => setVideoOpen(true)}
            data-testid="play-video-btn"
            title={t('video.play')}
            className="flex h-9 items-center gap-1.5 rounded-lg border border-asgym-accent/40 bg-asgym-accent/10 px-3 text-[11px] font-bold text-asgym-accent transition-transform active:scale-95"
          >
            <Play size={14} fill="currentColor" /> {t('lib.video')}
          </button>
        ) : (
          <span className="flex h-9 items-center gap-1.5 rounded-lg border border-white/[0.06] px-3 text-[11px] font-bold text-asgym-muted" data-testid="video-missing" title={t('video.noVideo')}>
            <Play size={14} /> —
          </span>
        )}
      </div>
      {hasVideo && <VideoPlayerModal open={videoOpen} onOpenChange={setVideoOpen} url={video} title={pick(ex, 'name', lang)} />}

      {pick(ex, 'coach_note', lang) && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-asgym-accent/20 bg-asgym-accent/[0.07] p-2.5">
          <MessageSquareText size={13} className="mt-0.5 shrink-0 text-asgym-accent" />
          <p className="text-[12px] leading-snug text-asgym-sub"><span className="font-bold text-asgym-accent">{t('training.coachTip')}: </span>{pick(ex, 'coach_note', lang)}</p>
        </div>
      )}

      <div className="mb-1 grid grid-cols-[28px_1fr_1fr_36px] gap-2 px-1 text-[9px] font-bold tracking-wide text-asgym-muted">
        <span>{t('training.setLabel')}</span><span>{t('training.kg')}</span><span>{t('training.reps')}</span><span></span>
      </div>
      <div className="space-y-1.5">
        {sets.map((s, i) => (
          <div key={i} className="grid grid-cols-[28px_1fr_1fr_36px] items-center gap-2">
            <span className="text-center text-xs font-black text-asgym-sub">{i + 1}</span>
            <input disabled={locked} className="as-input-sm text-center" inputMode="decimal" value={s.weight} onChange={(e) => upd(i, 'weight', e.target.value)} placeholder="—" data-testid={`set-weight-${i}`} />
            <input disabled={locked} className="as-input-sm text-center" inputMode="numeric" value={s.reps} onChange={(e) => upd(i, 'reps', e.target.value)} placeholder="—" data-testid={`set-reps-${i}`} />
            <button disabled={locked} onClick={() => upd(i, 'done', !s.done)} className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${s.done ? 'border-asgym-accent bg-asgym-accent text-black' : 'border-white/[0.08] bg-white/[0.03] text-asgym-muted'}`}>
              <Check size={16} />
            </button>
          </div>
        ))}
      </div>

      {!locked && (
        <button onClick={save} disabled={saving} data-testid="save-exercise-btn" className="as-btn-ghost mt-3 h-11 w-full text-xs">
          <Save size={15} /> {t('common.save')}
        </button>
      )}
    </div>
  );
}
